import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useThemePlayer } from '../../hooks/useThemePlayer';
import { SCREEN_TYPES } from '../screens';
import ProgressBar from './ProgressBar';
import Navigation from './Navigation';
import MilestonePopup from '../common/MilestonePopup';
import { useScore } from '../../context/ScoreProvider';
import '../../styles/ThemePlayer.css';
import Logo from '../common/Logo';

export default function ThemePlayer() {
  const { themeId } = useParams();
  const navigate = useNavigate();
  
  const {
    theme,
    currentScreen,
    screenNumber,
    totalScreens,
    progress,
    nextScreen,
    prevScreen,
    canGoNext,
    canGoPrev,
    startAgain
  } = useThemePlayer(themeId);
  const {
    totalScore,
    activeMilestone,
    hasPendingMilestone,
    activatePendingMilestone,
    dismissMilestone,
    resetScore
  } = useScore();

  const [quizState, setQuizState] = useState({
    checked: false,
    canCheck: false,
    onCheck: null,
  });

  useEffect(() => {
    document.querySelector('.screen-container')?.scrollTo(0, 0);
    
    setQuizState({
      checked: false,
      canCheck: false,
      onCheck: null,
    });
  }, [currentScreen?.id, screenNumber]);

  useEffect(() => {
    if (activeMilestone) {
      const container = document.querySelector('.screen-container');
      container?.scrollTo({
        top: 0,
        behavior: 'auto', // важливо: без анімації
      });
    }
  }, [activeMilestone]);

  useEffect(() => {
    const container = document.querySelector('.screen-container');
    if (!container) return undefined;

    container.classList.toggle('popup-active', !!activeMilestone);

    return () => {
      container.classList.remove('popup-active');
    };
  }, [activeMilestone]);

  if (!theme) {
    return (
      <div className="theme-error">
        <div className="error-content">
          <h1>😕 Тема не знайдена</h1>
          <p>На жаль, тема "{themeId}" не існує</p>
          <button onClick={() => navigate('/')}>
            Повернутися на головну
          </button>
        </div>
      </div>
    );
  }

  if (!currentScreen) {
    return (
      <div className="theme-error">
        <div className="error-content">
          <h1>😕 Екран не знайдено</h1>
          <button onClick={() => navigate('/')}>
            Повернутися на головну
          </button>
        </div>
      </div>
    );
  }

  const ScreenComponent = SCREEN_TYPES[currentScreen.type];

  if (!ScreenComponent) {
    return (
      <div className="theme-error">
        <div className="error-content">
          <h1>😕 Невідомий тип екрану</h1>
          <p>Тип "{currentScreen.type}" не підтримується</p>
          <button onClick={() => navigate('/')}>
            Повернутися на головну
          </button>
        </div>
      </div>
    );
  }

  const navigationConfig = currentScreen.navigation || {};

  const isQuiz = currentScreen.type === 'quiz' || 
    currentScreen.type === 'basic-quiz' ||
    currentScreen.type === 'input-quiz' ||
    currentScreen.type === 'range-quiz' ||
    currentScreen.type === 'multiple-quiz';

  const isFractionStory = currentScreen.type === 'fraction-story';
  const isCheckableScreen = isQuiz || isFractionStory;
  const isScrollGatedComicsScreen = currentScreen.type.startsWith('fraction-comics');
  const hasScrollPromptButton = [
    'fraction-comics-intro',
    'fraction-story',
    'fraction-comics-1',
    'fraction-comics-2',
    'fraction-comics-3',
    'fraction-comics-4',
  ].includes(currentScreen.type);

  const resolvedCanGoNext = activeMilestone
    ? true
    : isCheckableScreen
    ? quizState.checked || quizState.canCheck
    : isScrollGatedComicsScreen
      ? quizState.canCheck
      : canGoNext;

  const buttonText = isCheckableScreen
    ? quizState.checked
      ? 'Далі'
      : 'Перевірити'
    : navigationConfig.buttonText;

  const resolvedButtonText =
    !resolvedCanGoNext && hasScrollPromptButton ? 'Гортай' :
    activeMilestone ? 'Продовжити' :
    buttonText;

  const handleNextClick = () => {
    if (activeMilestone) {
      dismissMilestone();
      nextScreen();
      return;
    }

    if (isCheckableScreen && !quizState.checked) {
      quizState.onCheck?.();
    } else if (isCheckableScreen && hasPendingMilestone) {
      activatePendingMilestone();
    } else {
      nextScreen();
    }
  };

  const handleRestart = () => {
    resetScore();
    startAgain();
  };

  const screenProps = {
    ...currentScreen.content,
    onComplete: nextScreen,
    onCheckStateChange: setQuizState,
    screenId: currentScreen.id,
    screenScore: currentScreen.score
  };

  if (currentScreen.type === 'final') {
    screenProps.score = totalScore;
  }

  return (
    <div className="theme-player">

    <div className="site-nav">
      {/* <img src={logo} alt="Логотип" className="header__logo-img" /> */}
      <Link to="/"> 
        <Logo className="header__logo-img" />
      </Link>  
    </div>
      
      {/* Поточний екран */}
      <div className={`screen-container screen-${currentScreen.type}`}>
        {/* Прогрес-бар */}
        <ProgressBar 
          current={screenNumber} 
          total={totalScreens} 
          progress={progress}
          onGoBack={prevScreen}
          canGoBack={canGoPrev}
        />

        <ScreenComponent 
          key={currentScreen.id}
          {...screenProps}
        />

        <MilestonePopup milestone={activeMilestone} />
      </div>
      
      {/* Навігація */}
      <Navigation 
        {...navigationConfig}   
        // onNext={nextScreen}
        // onPrev={prevScreen}
        // canGoNext={canGoNext}
        // canGoPrev={canGoPrev}
        // onHome={() => navigate('/')}
        buttonText={resolvedButtonText}
        onNext={handleNextClick}
        canGoNext={resolvedCanGoNext}
        onPrev={prevScreen}
        onHome={() => navigate('/')}
        onRestart={handleRestart}
      />

    </div>
  );
}
